import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import type {
  Account,
  BatchAttachment,
  BatchTotals,
  OcrClassifyResponse,
  ParsedWorkbookResponse,
  Party,
  RawUplineRow,
  Settlement,
  SettlementAdvisory,
  VarianceSeverity,
  VerifyBlocker,
  WeeklyBatch,
} from '../api';
import { Upload } from 'lucide-react';
import { AppShell } from '../ui/AppShell';
import { useReadOnly } from '../ui/AppShell';
import { PageToolbar } from '../ui/PageToolbar';
import { Btn, Card, StatePill } from '../ui/components';
import {
  Button,
  ErrorBanner,
  Spinner,
} from '../ui/primitives';
import { UnmatchedPanel } from '../batch/UnmatchedPanel';
import { SettlementRow } from '../batch/SettlementRow';
import { ImportDialog } from '../import/ImportDialog';
import { OcrAliasStep } from '../import/OcrAliasStep';
import { ManualEntryGrid } from '../import/ManualEntryGrid';
import { usePinGate } from '../auth/pinGate';

export function BatchDetailPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const batchId = Number(idParam);
  const nav = useNavigate();
  const qc = useQueryClient();
  const readOnly = useReadOnly();

  const batch = useQuery<WeeklyBatch>({
    queryKey: ['batch', batchId],
    queryFn: () => api.get<WeeklyBatch>(`/api/batches/${batchId}`),
    enabled: Number.isFinite(batchId),
  });
  const attachments = useQuery<BatchAttachment[]>({
    queryKey: ['batch', batchId, 'attachments'],
    queryFn: () => api.get<BatchAttachment[]>(`/api/batches/${batchId}/attachments`),
    enabled: Number.isFinite(batchId),
  });
  const rawRows = useQuery<RawUplineRow[]>({
    queryKey: ['batch', batchId, 'raw-rows'],
    queryFn: () => api.get<RawUplineRow[]>(`/api/batches/${batchId}/raw-rows`),
    enabled: Number.isFinite(batchId),
  });
  const settlements = useQuery<Settlement[]>({
    queryKey: ['batch', batchId, 'settlements'],
    queryFn: () => api.get<Settlement[]>(`/api/batches/${batchId}/settlements`),
    enabled: Number.isFinite(batchId),
  });
  const totals = useQuery<BatchTotals>({
    queryKey: ['batch', batchId, 'totals'],
    queryFn: () => api.get<BatchTotals>(`/api/batches/${batchId}/totals`),
    enabled: Number.isFinite(batchId),
  });
  const advisories = useQuery<SettlementAdvisory[]>({
    queryKey: ['batch', batchId, 'advisories'],
    queryFn: () => api.get<SettlementAdvisory[]>(`/api/batches/${batchId}/advisories`),
    enabled: Number.isFinite(batchId),
  });
  const blockers = useQuery<VerifyBlocker[]>({
    queryKey: ['batch', batchId, 'blockers'],
    queryFn: () => api.get<VerifyBlocker[]>(`/api/batches/${batchId}/blockers`),
    enabled: Number.isFinite(batchId),
  });
  const accountsQ = useQuery<Account[]>({
    queryKey: ['accounts'],
    queryFn: () => api.get<Account[]>('/api/accounts'),
  });
  const clientsQ = useQuery<Party[]>({
    queryKey: ['parties', 'client'],
    queryFn: () => api.get<Party[]>('/api/parties?role=client'),
  });
  const uplinesQ = useQuery<Party[]>({
    queryKey: ['parties', 'upline'],
    queryFn: () => api.get<Party[]>('/api/parties?role=upline'),
  });
  const features = useQuery<{ ocrEnabled: boolean }>({
    queryKey: ['settings', 'features'],
    queryFn: () => api.get<{ ocrEnabled: boolean }>('/api/settings/features'),
    staleTime: 60_000,
  });

  const [importOpen, setImportOpen] = useState(false);
  const [filterSeverity, setFilterSeverity] = useState<'' | VarianceSeverity>('');
  const [filterClient, setFilterClient] = useState<string>('');
  const [genericError, setGenericError] = useState<string | null>(null);
  const [ocrPreseeded, setOcrPreseeded] = useState<ParsedWorkbookResponse | null>(null);
  const [ocrAliasData, setOcrAliasData] = useState<OcrClassifyResponse | null>(null);
  const [allowedAccountNames, setAllowedAccountNames] = useState<Set<string> | undefined>();
  const [manualEntryOpen, setManualEntryOpen] = useState(false);
  const { withPin } = usePinGate();

  const ocrClassifyMut = useMutation({
    mutationFn: (attachmentId: number) =>
      api.post<OcrClassifyResponse>(
        `/api/batches/${batchId}/attachments/${attachmentId}/ocr-classify`,
      ),
    onSuccess: (data) => {
      setGenericError(null);
      if (data.fallback) {
        setGenericError(
          'Vision AI failed to parse structured data — fell back to basic OCR. ' +
          'Results may be less accurate. If this looks wrong, use manual entry instead.',
        );
      }
      setOcrAliasData(data);
    },
    onError: (e) => setGenericError(e instanceof ApiError ? e.message : String(e)),
  });

  const computeMut = useMutation({
    mutationFn: () => api.post<unknown>(`/api/batches/${batchId}/compute`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['batch', batchId] }),
    onError: (e) => setGenericError(e instanceof ApiError ? e.message : String(e)),
  });

  const verifyMut = useMutation({
    mutationFn: () => api.post<WeeklyBatch>(`/api/batches/${batchId}/verify`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['batch', batchId] }),
    onError: (e) => setGenericError(e instanceof ApiError ? e.message : String(e)),
  });
  const unlockMut = useMutation({
    mutationFn: () => api.post<WeeklyBatch>(`/api/batches/${batchId}/unlock`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['batch', batchId] }),
    onError: (e) => setGenericError(e instanceof ApiError ? e.message : String(e)),
  });
  const postMut = useMutation({
    mutationFn: () =>
      api.post<{ batch: WeeklyBatch; entriesCreated: number; settlementsLocked: number }>(
        `/api/batches/${batchId}/post`,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['batch', batchId] }),
    onError: (e) => setGenericError(e instanceof ApiError ? e.message : String(e)),
  });
  const unpostMut = useMutation({
    mutationFn: (reason: string) =>
      api.post<{ batch: WeeklyBatch; entriesReversed: number; settlementsUnlocked: number }>(
        `/api/batches/${batchId}/unpost`,
        { reason },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['batch', batchId] }),
    onError: (e) => setGenericError(e instanceof ApiError ? e.message : String(e)),
  });
  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/api/batches/${batchId}`),
    onSuccess: () => nav('/batches'),
    onError: (e) => setGenericError(e instanceof ApiError ? e.message : String(e)),
  });

  const accountsById = useMemo(
    () => new Map((accountsQ.data ?? []).map((a) => [a.id, a])),
    [accountsQ.data],
  );
  const clientsById = useMemo(
    () => new Map((clientsQ.data ?? []).map((c) => [c.id, c])),
    [clientsQ.data],
  );

  if (!Number.isFinite(batchId)) {
    return (
      <AppShell title="Bad batch id">
        <Card>
          <ErrorBanner>Not a valid batch id.</ErrorBanner>
        </Card>
      </AppShell>
    );
  }

  if (batch.isLoading) {
    return (
      <AppShell title="Loading…">
        <div className="flex items-center gap-2 text-small text-ink-muted">
          <Spinner /> loading…
        </div>
      </AppShell>
    );
  }

  if (!batch.data) {
    return (
      <AppShell title="Not found">
        <Card>
          <ErrorBanner>Batch {batchId} not found.</ErrorBanner>
        </Card>
      </AppShell>
    );
  }

  const uplineName =
    uplinesQ.data?.find((p) => p.id === batch.data.uplinePartyId)?.displayName ??
    `#${batch.data.uplinePartyId}`;
  const editable = batch.data.state !== 'archived';

  // Group settlements by client party (mirrors Google Sheet layout).
  const filtered = (settlements.data ?? []).filter((s) => {
    if (filterSeverity && s.varianceSeverity !== filterSeverity) return false;
    if (filterClient) {
      if (filterClient === 'self' && s.clientPartyId !== null) return false;
      if (filterClient !== 'self' && String(s.clientPartyId ?? '') !== filterClient) return false;
    }
    return true;
  });
  // Key: clientPartyId (null → 'self')
  const byClient = new Map<string, Settlement[]>();
  for (const s of filtered) {
    const key = s.clientPartyId !== null ? String(s.clientPartyId) : 'self';
    if (!byClient.has(key)) byClient.set(key, []);
    byClient.get(key)!.push(s);
  }
  for (const list of byClient.values()) {
    list.sort((a, b) => {
      const an = accountsById.get(a.accountId)?.canonicalName ?? '';
      const bn = accountsById.get(b.accountId)?.canonicalName ?? '';
      return an.localeCompare(bn);
    });
  }
  // Sort client groups: named clients alphabetically, 'self' last
  const clientGroupOrder = Array.from(byClient.keys()).sort((a, b) => {
    if (a === 'self') return 1;
    if (b === 'self') return -1;
    const na = clientsById.get(Number(a))?.displayName ?? '';
    const nb = clientsById.get(Number(b))?.displayName ?? '';
    return na.localeCompare(nb);
  });

  const pageTitle = `Batch #${batch.data.id} — ${uplineName} — ${batch.data.weekMonday}`;
  return (
    <AppShell
      title={pageTitle}
      toolbar={
        <PageToolbar
          back={{ to: '/batches', label: 'All batches' }}
          title={pageTitle}
          primary={
            editable && !readOnly ? (
              <Btn size="sm" onClick={() => setImportOpen(true)}>
                <Upload className="h-3.5 w-3.5" strokeWidth={2} />
                <span className="hidden sm:inline">Import rows</span>
              </Btn>
            ) : undefined
          }
        />
      }
    >
      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-4">
          {genericError && <ErrorBanner>{genericError}</ErrorBanner>}

          {/* Blockers */}
          {blockers.data && blockers.data.length > 0 && (
            <div className="rounded-card border border-neg/30 bg-neg/5 p-4">
              <h3 className="mb-2 text-small font-semibold text-neg">
                Blockers ({blockers.data.length})
              </h3>
              <ul className="space-y-1 text-caption text-neg">
                {blockers.data.map((b, i) => (
                  <li key={i}>
                    <span className="font-semibold">[{b.kind}]</span> {b.detail}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <UnmatchedPanel batchId={batchId} />

          {/* ── Settlements ──────────────────────────────────────────────── */}
          <Card>
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="text-small font-semibold text-ink">Settlements</h3>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  aria-label="Variance filter"
                  className="min-h-[36px] rounded-md border border-border bg-surface px-2 py-1 text-small text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                  value={filterSeverity}
                  onChange={(e) => setFilterSeverity(e.target.value as '' | VarianceSeverity)}
                >
                  <option value="">All variance</option>
                  <option value="NONE">NONE</option>
                  <option value="MINOR">MINOR</option>
                  <option value="REVIEW">REVIEW</option>
                  <option value="MAJOR">MAJOR</option>
                </select>
                <select
                  aria-label="Client filter"
                  className="min-h-[36px] rounded-md border border-border bg-surface px-2 py-1 text-small text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                  value={filterClient}
                  onChange={(e) => setFilterClient(e.target.value)}
                >
                  <option value="">All clients</option>
                  <option value="self">Self</option>
                  {(clientsQ.data ?? []).map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.displayName}
                    </option>
                  ))}
                </select>
                <span className="text-caption text-ink-muted">
                  {filtered.length}/{settlements.data?.length ?? 0} shown
                </span>
              </div>
            </div>

            {/* Desktop: full settlements table */}
            <div className="mt-3 hidden overflow-x-auto md:block">
              <table className="min-w-full text-small">
                <thead className="sticky top-0 z-10 bg-surface-raised text-caption uppercase tracking-wide text-ink-muted">
                  <tr>
                    <th className="px-3 py-2 text-left">Account</th>
                    {/* ── client side ─────────────────────────── */}
                    <th className="border-l border-border/50 px-3 py-2 text-right text-info/70">Client pts</th>
                    <th className="px-3 py-2 text-right text-info/70">C.rate</th>
                    <th className="px-3 py-2 text-right text-info/70">Client amt</th>
                    {/* ── upline side ─────────────────────────── */}
                    <th className="border-l border-border/50 px-3 py-2 text-right text-brand/70">Upline pts</th>
                    <th className="px-3 py-2 text-right text-brand/70">U.rate</th>
                    <th className="px-3 py-2 text-right text-brand/70">Upline cost</th>
                    {/* ── result ──────────────────────────────── */}
                    <th className="border-l border-border/50 px-3 py-2 text-right">Margin</th>
                    <th className="px-3 py-2 text-center">Var.</th>
                    <th className="px-3 py-2 text-left">Flags</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {clientGroupOrder.map((clientKey) => (
                    <ClientGroupRows
                      key={clientKey}
                      clientKey={clientKey}
                      rows={byClient.get(clientKey)!}
                      accountsById={accountsById}
                      clientsById={clientsById}
                      advisories={advisories.data ?? []}
                      editable={editable && !readOnly}
                      onEdited={() => {
                        void qc.invalidateQueries({ queryKey: ['batch', batchId] });
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: settlement cards grouped by client */}
            <div className="mt-3 space-y-4 md:hidden">
              {clientGroupOrder.map((clientKey) => {
                const clientName =
                  clientKey === 'self'
                    ? 'Self (company)'
                    : (clientsById.get(Number(clientKey))?.displayName ?? `Client #${clientKey}`);
                const groupRows = byClient.get(clientKey)!;
                return (
                  <div key={clientKey}>
                    <div className="mb-2 px-1 text-caption font-semibold uppercase tracking-wide text-brand">
                      {clientName}
                      <span className="ml-1.5 font-normal normal-case tracking-normal text-brand/70">
                        ({groupRows.length} account{groupRows.length !== 1 ? 's' : ''})
                      </span>
                    </div>
                    <div className="space-y-2">
                      {groupRows.map((s) => (
                        <SettlementMobileCard
                          key={s.id}
                          settlement={s}
                          accountsById={accountsById}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {(settlements.data?.length ?? 0) === 0 && (
              <p className="mt-3 text-caption text-ink-muted">
                No settlements yet. Import raw rows, then compute.
              </p>
            )}

            {/* Action buttons — hidden for read-only users */}
            {!readOnly && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {batch.data.state !== 'archived' && (
                  <Btn onClick={() => computeMut.mutate()} loading={computeMut.isPending}>
                    Compute / recompute
                  </Btn>
                )}
                {batch.data.state === 'draft' && (
                  <Btn
                    onClick={() => verifyMut.mutate()}
                    loading={verifyMut.isPending}
                    disabled={(blockers.data?.length ?? 0) > 0}
                  >
                    Verify batch
                  </Btn>
                )}
                {batch.data.state === 'verified' && (
                  <>
                    <Btn
                      onClick={() => {
                        const settlementsCount = settlements.data?.length ?? 0;
                        const ok = confirm(
                          `Post this batch to ledgers?\n\n` +
                            `${settlementsCount} settlement(s) will be written as immutable ledger entries ` +
                            `across client, upline, and self ledgers. Posted settlements cannot be edited ` +
                            `until the batch is unposted.\n\nContinue?`,
                        );
                        if (ok) postMut.mutate();
                      }}
                      loading={postMut.isPending}
                      disabled={(blockers.data?.length ?? 0) > 0}
                    >
                      Post to ledgers
                    </Btn>
                    <Btn
                      variant="secondary"
                      onClick={() => void withPin(() => unlockMut.mutateAsync())}
                      loading={unlockMut.isPending}
                    >
                      Unlock for correction
                    </Btn>
                  </>
                )}
                {batch.data.state === 'archived' && (
                  <Btn
                    variant="danger"
                    onClick={() => {
                      const reason = prompt(
                        'Reason for unposting this batch?\n\n' +
                          'This will reverse every ledger entry created by this post. ' +
                          'Balances return to pre-post state.',
                      );
                      if (reason && reason.trim().length > 0) {
                        void withPin(() => unpostMut.mutateAsync(reason.trim()));
                      }
                    }}
                    loading={unpostMut.isPending}
                  >
                    Unpost batch
                  </Btn>
                )}
                {batch.data.state !== 'archived' && (
                  <Btn
                    variant="danger"
                    onClick={() => {
                      const ok = confirm(
                        `Delete this batch?\n\nAll raw rows, settlements, and attachments will be permanently removed. This cannot be undone.`,
                      );
                      if (ok) deleteMut.mutate();
                    }}
                    loading={deleteMut.isPending}
                  >
                    Delete batch
                  </Btn>
                )}
              </div>
            )}
          </Card>
        </div>

        {/* ── Sidebar ────────────────────────────────────────────────────── */}
        <aside className="space-y-4">
          <BatchNotesCard
            batchId={batchId}
            initialNotes={batch.data.notes}
            readOnly={readOnly}
            onError={(msg) => setGenericError(msg)}
          />

          {/* Summary */}
          <Card header={{ title: 'Summary' }}>
            {totals.data ? (
              <dl className="space-y-1.5 text-small">
                <DRow label="State">
                  <StatePill state={batch.data.state} />
                </DRow>
                <DRow label="Rows">{totals.data.rowCount}</DRow>
                <DRow label="Upline cost">
                  <span className="font-mono text-ink">{totals.data.uplineCostSum}</span>
                </DRow>
                <DRow label="Client amount">
                  <span className="font-mono text-ink">{totals.data.clientAmountSum}</span>
                </DRow>
                <DRow label="Your margin">
                  <span className="font-mono font-semibold text-pos">{totals.data.userMarginSum}</span>
                </DRow>
                <DRow label="MAJOR variances">
                  <span className={totals.data.majorVarianceCount > 0 ? 'text-neg font-semibold' : 'text-ink'}>
                    {totals.data.majorVarianceCount}
                  </span>
                </DRow>
                <DRow label="Pending">{totals.data.pendingCount}</DRow>
              </dl>
            ) : (
              <p className="text-caption text-ink-muted">No totals yet.</p>
            )}
          </Card>

          {/* Attachments */}
          <Card header={{ title: 'Attachments' }}>
            {attachments.data && attachments.data.length > 0 ? (
              <ul className="space-y-2">
                {attachments.data.map((a) => (
                  <li key={a.id} className="rounded-md border border-border p-2.5">
                    <div className="flex items-center justify-between gap-1">
                      <span className="truncate text-small font-medium text-ink">
                        {a.originalFilename}
                      </span>
                      <span className="shrink-0 text-caption text-ink-muted">
                        {(a.byteSize / 1024).toFixed(0)} kB
                      </span>
                    </div>
                    <div className="mt-1 flex items-center justify-between">
                      <span className="font-mono text-caption text-ink-muted">
                        {a.sha256.slice(0, 12)}…
                      </span>
                      <a
                        href={`/api/attachments/${a.sha256}${attachmentExt(a.originalFilename)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-caption text-brand hover:text-brand-dark hover:underline"
                      >
                        {isImageMimeType(a.mimeType) ? 'View' : 'Download'}
                      </a>
                    </div>
                    {features.data?.ocrEnabled &&
                      isImageMimeType(a.mimeType) &&
                      editable &&
                      !readOnly && (
                        <div className="mt-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="px-2 py-1 text-small"
                            onClick={() => {
                              setGenericError(null);
                              ocrClassifyMut.mutate(a.id);
                            }}
                            disabled={ocrClassifyMut.isPending}
                          >
                            {ocrClassifyMut.isPending &&
                            ocrClassifyMut.variables === a.id ? (
                              <Spinner />
                            ) : (
                              'Extract from image'
                            )}
                          </Button>
                        </div>
                      )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-caption text-ink-muted">No attachments yet.</p>
            )}

            {/* Attachments are metadata — always allow adding, even on posted batches */}
            {!readOnly && (
              <div className="mt-3 border-t border-border pt-3">
                <AttachmentUploader
                  batchId={batchId}
                  onUploaded={() =>
                    void qc.invalidateQueries({ queryKey: ['batch', batchId, 'attachments'] })
                  }
                  onError={(msg) => setGenericError(msg)}
                />
              </div>
            )}

            {editable && !readOnly && (
              <div className="mt-3 border-t border-border pt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-center"
                  onClick={() => setManualEntryOpen((o) => !o)}
                >
                  {manualEntryOpen ? 'Close manual entry ↑' : 'Manual entry ↓'}
                </Button>
              </div>
            )}
          </Card>

          {/* Raw rows */}
          <Card header={{ title: 'Raw rows imported' }}>
            <p className="text-small text-ink-muted">
              {rawRows.data?.length ?? 0} row(s).
              {(rawRows.data ?? []).filter((r) => r.matchedAccountId === null && !r.ignored).length > 0 && (
                <span className="ml-1 font-medium text-warn">
                  {(rawRows.data ?? []).filter((r) => r.matchedAccountId === null && !r.ignored).length}{' '}
                  unmatched
                </span>
              )}
            </p>
          </Card>
        </aside>
      </div>

      {editable && !readOnly && manualEntryOpen && batch.data && (
        <Card className="mt-4">
          <h3 className="mb-3 text-small font-semibold text-ink">Manual entry</h3>
          <ManualEntryGrid
            batchId={batchId}
            uplinePartyId={batch.data.uplinePartyId}
            onClose={() => setManualEntryOpen(false)}
            onSaved={(count) => {
              setManualEntryOpen(false);
              setGenericError(null);
              void qc.invalidateQueries({ queryKey: ['batch', batchId] });
              const prev = document.title;
              document.title = `✓ Saved ${count} row(s)`;
              setTimeout(() => { document.title = prev; }, 3000);
            }}
          />
        </Card>
      )}

      {ocrAliasData && (
        <OcrAliasStep
          data={ocrAliasData}
          onCancel={() => setOcrAliasData(null)}
          onConfirmed={(allowed, workbookData) => {
            setOcrAliasData(null);
            setAllowedAccountNames(allowed);
            setOcrPreseeded(workbookData);
            setImportOpen(true);
          }}
        />
      )}

      {importOpen && (
        <ImportDialog
          batchId={batchId}
          preSeeded={ocrPreseeded ?? undefined}
          allowedAccountNames={allowedAccountNames}
          onClose={() => {
            setImportOpen(false);
            setOcrPreseeded(null);
            setAllowedAccountNames(undefined);
          }}
          onImported={() => {
            setImportOpen(false);
            setOcrPreseeded(null);
            setAllowedAccountNames(undefined);
            void qc.invalidateQueries({ queryKey: ['batch', batchId] });
          }}
        />
      )}
    </AppShell>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function ClientGroupRows({
  clientKey,
  rows,
  accountsById,
  clientsById,
  advisories,
  editable,
  onEdited,
}: {
  clientKey: string;
  rows: Settlement[];
  accountsById: Map<number, Account>;
  clientsById: Map<number, Party>;
  advisories: SettlementAdvisory[];
  editable: boolean;
  onEdited: () => void;
}) {
  const clientName =
    clientKey === 'self'
      ? 'Self (company)'
      : (clientsById.get(Number(clientKey))?.displayName ?? `Client #${clientKey}`);

  // Per-client subtotals
  let uplineCostSum = 0;
  let clientAmountSum = 0;
  let marginSum = 0;
  for (const s of rows) {
    uplineCostSum += Number(s.uplineCost ?? 0);
    clientAmountSum += Number(s.clientAmount ?? 0);
    marginSum += Number(s.userMargin ?? 0);
  }

  return (
    <>
      {/* Client section header */}
      <tr className="bg-brand-light/40">
        <td
          colSpan={11}
          className="px-2 py-1.5 text-caption font-semibold uppercase tracking-wide text-brand"
        >
          {clientName}
          <span className="ml-2 font-normal normal-case tracking-normal text-brand/70">
            ({rows.length} account{rows.length !== 1 ? 's' : ''})
          </span>
        </td>
      </tr>
      {rows.map((s) => (
        <SettlementRow
          key={s.id}
          settlement={s}
          accountsById={accountsById}
          clientsById={clientsById}
          advisories={advisories}
          editable={editable}
          onEdited={onEdited}
        />
      ))}
      {/* Per-client subtotal */}
      <tr className="border-t border-border bg-surface-raised text-caption font-semibold">
        <td className="px-3 py-1.5 italic text-ink-muted">{clientName} total</td>
        {/* client side: pts, rate, amt */}
        <td className="border-l border-border/50 px-3 py-1.5" />
        <td className="px-3 py-1.5" />
        <td className="px-3 py-1.5 text-right font-mono text-info">
          {clientAmountSum.toFixed(3)}
        </td>
        {/* upline side: pts, rate, cost */}
        <td className="border-l border-border/50 px-3 py-1.5" />
        <td className="px-3 py-1.5" />
        <td className="px-3 py-1.5 text-right font-mono text-brand">
          {uplineCostSum.toFixed(3)}
        </td>
        {/* margin */}
        <td className={`border-l border-border/50 px-3 py-1.5 text-right font-mono ${marginSum >= 0 ? 'text-pos' : 'text-neg'}`}>
          {marginSum.toFixed(3)}
        </td>
        <td colSpan={3} />
      </tr>
    </>
  );
}

function DRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-ink">{children}</dd>
    </div>
  );
}

function SettlementMobileCard({
  settlement,
  accountsById,
}: {
  settlement: Settlement;
  accountsById: Map<number, Account>;
}) {
  const accountName =
    accountsById.get(settlement.accountId)?.canonicalName ?? `#${settlement.accountId}`;
  const margin = Number(settlement.userMargin ?? 0);
  return (
    <div className="rounded-md border border-border bg-surface p-3 text-small">
      <div className="flex items-start justify-between gap-2">
        <span className="font-medium text-ink">{accountName}</span>
        {settlement.varianceSeverity && settlement.varianceSeverity !== 'NONE' && (
          <StatePill state={settlement.varianceSeverity.toLowerCase()} />
        )}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-caption">
        <span className="text-ink-muted">Client amount</span>
        <span className="text-right font-mono text-ink">
          {settlement.clientAmount ?? '—'}
        </span>
        <span className="text-ink-muted">Margin</span>
        <span
          className={`text-right font-mono font-semibold ${
            margin >= 0 ? 'text-pos' : 'text-neg'
          }`}
        >
          {settlement.userMargin ?? '—'}
        </span>
      </div>
    </div>
  );
}

function isImageMimeType(mimeType: string): boolean {
  return mimeType === 'image/jpeg' || mimeType === 'image/png';
}

function attachmentExt(filename: string): string {
  const m = /(\.[a-zA-Z0-9]{1,10})$/.exec(filename);
  return m?.[1]?.toLowerCase() ?? '';
}

/**
 * Uploader for additional attachments on an already-open batch.
 */
function AttachmentUploader({
  batchId,
  onUploaded,
  onError,
}: {
  batchId: number;
  onUploaded: () => void;
  onError: (msg: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);

  const upload = async (files: File[]) => {
    if (files.length === 0) return;
    setBusy(true);
    try {
      for (const f of files) {
        const base64 = await fileToBase64(f);
        await api.post<unknown>(`/api/batches/${batchId}/attachments`, {
          base64,
          originalFilename: f.name,
          mimeType: f.type || 'application/octet-stream',
        });
      }
      onUploaded();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.pdf,.xlsx"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          void upload(files);
        }}
      />
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-center"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? <Spinner /> : '+ Add attachment'}
      </Button>
    </>
  );
}

function fileToBase64(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(f);
  });
}

/**
 * Inline-editable batch notes. Saves on blur.
 */
function BatchNotesCard({
  batchId,
  initialNotes,
  readOnly,
  onError,
}: {
  batchId: number;
  initialNotes: string | null;
  readOnly: boolean;
  onError: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState(initialNotes ?? '');
  const [showSaved, setShowSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const saveMut = useMutation({
    mutationFn: (notes: string | null) =>
      api.patch<WeeklyBatch>(`/api/batches/${batchId}`, { notes }),
    onMutate: () => setSaving(true),
    onSuccess: () => {
      setSaving(false);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 1500);
      void qc.invalidateQueries({ queryKey: ['batch', batchId] });
    },
    onError: (e) => {
      setSaving(false);
      onError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const onBlur = () => {
    if (readOnly) return;
    const next = draft.trim().length === 0 ? null : draft;
    const prev = initialNotes ?? '';
    if ((next ?? '') === prev) return; // no change
    saveMut.mutate(next);
  };

  return (
    <Card>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-small font-semibold text-ink">Notes</h3>
        {saving ? (
          <span className="text-caption text-ink-muted">saving…</span>
        ) : showSaved ? (
          <span className="text-caption text-pos">saved ✓</span>
        ) : null}
      </div>
      <textarea
        rows={3}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={onBlur}
        readOnly={readOnly}
        placeholder="e.g. SRB changed rates Wednesday; double-check row 7"
        className="w-full resize-y rounded-md border border-border bg-surface px-2 py-1.5 text-small text-ink placeholder:text-ink-muted focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30 read-only:cursor-default read-only:bg-surface-raised"
      />
      <p className="mt-1 text-caption text-ink-muted">
        Metadata only — doesn&apos;t affect amounts or balances.
      </p>
    </Card>
  );
}
